// ─── MomentsGallery ──────────────────────────────────────────
// Dashboard tab: overview + light control of guest photo moments.
// 3-column grid (desktop), 2-column (mobile), square 1:1 thumbnails.
// Hover: scale + trash + heart. Delete with confirmation.
// Favorites silently influence the reveal selection.

import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { Trash2, X, ChevronLeft, ChevronRight, Heart } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export default function MomentsGallery({ eventId, token, adminDarkMode }) {
  const [moments, setMoments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewerIndex, setViewerIndex] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [fadingOut, setFadingOut] = useState(null);
  const viewerRef = useRef(null);

  // ─── Curated selection mode ──
  const [selectMode, setSelectMode] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState([]); // array of moment IDs in order
  const MAX_SELECT = 15;

  const fetchMoments = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/events/${eventId}/guest-moments`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setMoments(res.data || []);
      // Load saved curated order if exists
      const orderRes = await axios.get(`${API}/api/events/${eventId}/reveal-order`, {
        headers: { Authorization: `Bearer ${token}` },
      }).catch(() => null);
      if (orderRes?.data?.order?.length) {
        setSelectedOrder(orderRes.data.order);
      }
    } catch { /* */ }
    setLoading(false);
  }, [eventId, token]);

  useEffect(() => { fetchMoments(); }, [fetchMoments]);

  const resolveUrl = (url) => url?.startsWith('/') ? `${API}${url}` : url;

  // ─── Delete with animation ──
  const handleDeleteConfirm = async (momentId) => {
    setFadingOut(momentId);
    try {
      await axios.delete(`${API}/api/events/${eventId}/guest-moments/${momentId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setTimeout(() => {
        setMoments(prev => prev.filter(m => m.id !== momentId));
        setFadingOut(null);
        setConfirmDelete(null);
        if (viewerIndex !== null) setViewerIndex(null);
      }, 300);
    } catch {
      setFadingOut(null);
      setConfirmDelete(null);
    }
  };

  // ─── Favorite toggle ──
  const handleFavorite = async (e, momentId) => {
    e.stopPropagation();
    try {
      const res = await axios.put(`${API}/api/events/${eventId}/guest-moments/${momentId}/favorite`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setMoments(prev => prev.map(m => m.id === momentId ? { ...m, favorited: res.data.favorited } : m));
    } catch { /* */ }
  };

  // ─── Curated selection ──
  const handleSelectTap = (momentId) => {
    const idx = selectedOrder.indexOf(momentId);
    if (idx >= 0) {
      // Deselect: remove and renumber
      setSelectedOrder(prev => prev.filter(id => id !== momentId));
    } else if (selectedOrder.length < MAX_SELECT) {
      setSelectedOrder(prev => [...prev, momentId]);
    }
  };

  const handleSaveOrder = async () => {
    try {
      await axios.put(`${API}/api/events/${eventId}/reveal-order`,
        { order: selectedOrder },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSelectMode(false);
    } catch { /* */ }
  };

  const handleCancelSelect = () => {
    setSelectMode(false);
    // Reload to restore saved order
    fetchMoments();
  };

  const handleMoveEarlier = (momentId) => {
    setSelectedOrder(prev => {
      const idx = prev.indexOf(momentId);
      if (idx <= 0) return prev;
      const next = [...prev];
      [next[idx - 1], next[idx]] = [next[idx], next[idx - 1]];
      return next;
    });
  };

  const handleMoveLater = (momentId) => {
    setSelectedOrder(prev => {
      const idx = prev.indexOf(momentId);
      if (idx < 0 || idx >= prev.length - 1) return prev;
      const next = [...prev];
      [next[idx], next[idx + 1]] = [next[idx + 1], next[idx]];
      return next;
    });
  };

  const handleRemoveFromOrder = (momentId) => {
    setSelectedOrder(prev => prev.filter(id => id !== momentId));
  };

  // Keyboard nav for viewer
  useEffect(() => {
    if (viewerIndex === null) return;
    const handler = (e) => {
      if (e.key === 'Escape') setViewerIndex(null);
      if (e.key === 'ArrowRight' && viewerIndex < moments.length - 1) setViewerIndex(i => i + 1);
      if (e.key === 'ArrowLeft' && viewerIndex > 0) setViewerIndex(i => i - 1);
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [viewerIndex, moments.length]);

  // ─── Empty State ──
  if (!loading && moments.length === 0) {
    return (
      <div className="p-16 text-center">
        <p className="text-sm" style={{ color: adminDarkMode ? '#64748b' : '#94a3b8' }}>
          Moments will appear here<br />as your guests share them
        </p>
      </div>
    );
  }

  // ─── Loading: skeleton ──
  if (loading) {
    return (
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3 p-4">
        {Array.from({ length: 9 }).map((_, i) => (
          <div
            key={i}
            className="aspect-square rounded-xl animate-pulse"
            style={{ backgroundColor: adminDarkMode ? '#1e293b' : '#f1f5f9' }}
          />
        ))}
      </div>
    );
  }

  const current = viewerIndex !== null ? moments[viewerIndex] : null;
  const limitReached = selectedOrder.length >= MAX_SELECT;

  return (
    <>
      {/* Header bar — curate entry or selection bar */}
      {selectMode ? (
        <div
          className="flex items-center justify-between mb-5 px-1"
          style={{ animation: 'curateBarFadeIn 200ms ease' }}
          data-testid="curate-top-bar"
        >
          <style>{`@keyframes curateBarFadeIn { from { opacity: 0; transform: translateY(-4px); } to { opacity: 1; transform: translateY(0); } }`}</style>
          <p
            className="text-[13px] tracking-wide"
            style={{ color: adminDarkMode ? '#cbd5e1' : '#475569', fontVariantNumeric: 'tabular-nums' }}
            data-testid="curate-counter"
          >
            {selectedOrder.length} / {MAX_SELECT} selected
          </p>
          <div className="flex items-center gap-5">
            <button
              onClick={handleCancelSelect}
              className="text-[13px] transition-colors"
              style={{ color: adminDarkMode ? '#64748b' : '#94a3b8' }}
              data-testid="curate-cancel"
            >
              Cancel
            </button>
            <button
              onClick={handleSaveOrder}
              disabled={selectedOrder.length === 0}
              className="text-[13px] font-medium transition-opacity"
              style={{
                color: adminDarkMode ? '#f8fafc' : '#0f172a',
                opacity: selectedOrder.length === 0 ? 0.35 : 1,
                cursor: selectedOrder.length === 0 ? 'default' : 'pointer',
              }}
              data-testid="curate-done"
            >
              Done
            </button>
          </div>
        </div>
      ) : (
        <div className="flex items-center justify-end mb-4 px-1">
          <button
            onClick={() => setSelectMode(true)}
            className="text-[12px] tracking-wide transition-colors hover:opacity-70"
            style={{ color: adminDarkMode ? '#94a3b8' : '#64748b' }}
            data-testid="curate-enter"
          >
            Curate reveal &rarr;
          </button>
        </div>
      )}

      {/* Grid — 3 columns desktop, 2 mobile, square 1:1 */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3 md:gap-4 px-1">
        {moments.map((m, i) => {
          const selIdx = selectMode ? selectedOrder.indexOf(m.id) : -1;
          const isSelected = selIdx >= 0;
          const dimmed = selectMode && !isSelected && limitReached;
          return (
          <div
            key={m.id}
            className={`relative aspect-square overflow-hidden rounded-xl ${selectMode ? '' : 'cursor-pointer group'}`}
            onClick={() => selectMode ? handleSelectTap(m.id) : setViewerIndex(i)}
            style={{
              opacity: fadingOut === m.id ? 0 : (dimmed ? 0.35 : 1),
              transform: fadingOut === m.id ? 'scale(0.95)' : 'scale(1)',
              transition: 'opacity 0.24s ease, transform 0.3s ease',
              cursor: selectMode ? (dimmed ? 'default' : 'pointer') : 'pointer',
            }}
            data-testid={`moment-tile-${m.id}`}
          >
            <img
              src={resolveUrl(m.image_url)}
              alt=""
              className={`w-full h-full object-cover object-center transition-transform duration-200 ${selectMode ? '' : 'group-hover:scale-[1.03]'}`}
              style={{ opacity: 0, transition: 'opacity 0.4s ease, transform 0.2s ease' }}
              onLoad={(e) => { e.target.style.opacity = '1'; }}
              loading="lazy"
              draggable={false}
            />

            {/* Hover overlay — desktop (disabled in select mode) */}
            {!selectMode && (
              <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors duration-200 pointer-events-none" />
            )}

            {/* Selection order circle — top-left, editorial, dark */}
            {selectMode && isSelected && (
              <div
                className="absolute top-2 left-2 rounded-full flex items-center justify-center z-20"
                style={{
                  width: 26,
                  height: 26,
                  backgroundColor: 'rgba(15,23,42,0.92)',
                  color: '#fff',
                  fontSize: 12,
                  fontWeight: 500,
                  fontVariantNumeric: 'tabular-nums',
                  letterSpacing: 0,
                  boxShadow: '0 2px 8px rgba(0,0,0,0.25)',
                  transform: 'scale(1)',
                  animation: 'curatePop 160ms cubic-bezier(0.34, 1.56, 0.64, 1)',
                }}
                data-testid={`moment-select-num-${m.id}`}
              >
                <style>{`@keyframes curatePop { from { opacity: 0; transform: scale(0.6); } to { opacity: 1; transform: scale(1); } }`}</style>
                {selIdx + 1}
              </div>
            )}

            {/* Favorite heart — top-left (hidden in select mode) */}
            {!selectMode && (
              <button
                onClick={(e) => handleFavorite(e, m.id)}
                className="absolute top-2 left-2 w-8 h-8 rounded-full flex items-center justify-center transition-all duration-200 opacity-0 group-hover:opacity-100 z-10"
                style={{
                  backgroundColor: m.favorited ? 'rgba(239,68,68,0.85)' : 'rgba(0,0,0,0.4)',
                  opacity: m.favorited ? 1 : undefined,
                }}
                data-testid={`moment-fav-${m.id}`}
              >
                <Heart
                  className="w-3.5 h-3.5 transition-transform"
                  style={{
                    color: '#fff',
                    fill: m.favorited ? '#fff' : 'none',
                    transform: m.favorited ? 'scale(1)' : 'scale(0.9)',
                  }}
                />
              </button>
            )}

            {/* Delete — top-right (hidden in select mode) */}
            {!selectMode && (
              <button
                onClick={(e) => { e.stopPropagation(); setConfirmDelete(m.id); }}
                className="absolute top-2 right-2 w-8 h-8 rounded-full bg-black/40 flex items-center justify-center text-white/80 opacity-0 group-hover:opacity-100 transition-all duration-200 hover:bg-red-500/80 z-10"
                data-testid={`moment-delete-${m.id}`}
              >
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            )}

            {/* Favorited indicator — subtle glow when not hovering (hidden in select mode) */}
            {!selectMode && m.favorited && (
              <div className="absolute top-2 left-2 w-8 h-8 rounded-full bg-red-500/80 flex items-center justify-center group-hover:opacity-0 transition-opacity z-10">
                <Heart className="w-3.5 h-3.5 text-white" style={{ fill: '#fff' }} />
              </div>
            )}

            {/* Info overlay on hover (hidden in select mode) */}
            {!selectMode && (
              <div className="absolute bottom-0 left-0 right-0 p-2.5 opacity-0 group-hover:opacity-100 transition-opacity duration-200 z-10"
                style={{ background: 'linear-gradient(to top, rgba(0,0,0,0.6) 0%, transparent 100%)' }}>
                {m.guest_name && (
                  <p className="text-white text-[11px] font-medium truncate">{m.guest_name}</p>
                )}
                {m.timestamp && (
                  <p className="text-white/50 text-[9px]">
                    {new Date(m.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                  </p>
                )}
              </div>
            )}
          </div>
          );
        })}
      </div>

      {/* Delete confirmation */}
      {confirmDelete && (
        <div className="fixed inset-0 z-[250] bg-black/60 flex items-center justify-center" onClick={() => setConfirmDelete(null)}>
          <div
            className="rounded-xl p-5 text-center max-w-xs mx-4"
            style={{ backgroundColor: adminDarkMode ? '#1e293b' : '#fff' }}
            onClick={e => e.stopPropagation()}
          >
            <p className="text-sm mb-4" style={{ color: adminDarkMode ? '#cbd5e1' : '#334155' }}>
              Remove this moment?
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => handleDeleteConfirm(confirmDelete)}
                className="flex-1 py-2 rounded-lg text-sm font-medium bg-red-500 text-white hover:bg-red-600 transition-colors"
              >
                Remove
              </button>
              <button
                onClick={() => setConfirmDelete(null)}
                className="flex-1 py-2 rounded-lg text-sm font-medium transition-colors"
                style={{
                  backgroundColor: adminDarkMode ? '#334155' : '#f1f5f9',
                  color: adminDarkMode ? '#cbd5e1' : '#475569',
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Fullscreen viewer */}
      {current && (
        <div
          ref={viewerRef}
          className="fixed inset-0 z-[200] bg-black/95 flex flex-col items-center justify-center"
          onClick={(e) => { if (e.target === viewerRef.current) setViewerIndex(null); }}
          style={{ animation: 'momentFadeIn 0.25s ease' }}
        >
          <style>{`@keyframes momentFadeIn { from { opacity: 0; } to { opacity: 1; } }`}</style>

          {/* Close */}
          <button
            onClick={() => setViewerIndex(null)}
            className="absolute top-4 right-4 w-10 h-10 rounded-full bg-white/10 flex items-center justify-center text-white/70 hover:text-white hover:bg-white/20 transition-colors z-10"
            data-testid="moments-viewer-close"
          >
            <X className="w-5 h-5" />
          </button>

          {/* Favorite in viewer */}
          <button
            onClick={(e) => handleFavorite(e, current.id)}
            className="absolute top-4 left-14 w-10 h-10 rounded-full flex items-center justify-center transition-colors z-10"
            style={{ backgroundColor: current.favorited ? 'rgba(239,68,68,0.8)' : 'rgba(255,255,255,0.1)' }}
          >
            <Heart className="w-4 h-4 text-white" style={{ fill: current.favorited ? '#fff' : 'none' }} />
          </button>

          {/* Delete in viewer */}
          <button
            onClick={() => setConfirmDelete(current.id)}
            className="absolute top-4 left-4 w-10 h-10 rounded-full bg-white/10 flex items-center justify-center text-red-400/70 hover:text-red-400 hover:bg-white/20 transition-colors z-10"
            data-testid="moments-viewer-delete"
          >
            <Trash2 className="w-4 h-4" />
          </button>

          {/* Nav arrows */}
          {viewerIndex > 0 && (
            <button
              onClick={(e) => { e.stopPropagation(); setViewerIndex(i => i - 1); }}
              className="absolute left-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/10 flex items-center justify-center text-white/60 hover:text-white hover:bg-white/20 transition-colors"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
          )}
          {viewerIndex < moments.length - 1 && (
            <button
              onClick={(e) => { e.stopPropagation(); setViewerIndex(i => i + 1); }}
              className="absolute right-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/10 flex items-center justify-center text-white/60 hover:text-white hover:bg-white/20 transition-colors"
            >
              <ChevronRight className="w-5 h-5" />
            </button>
          )}

          {/* Image — original ratio in viewer */}
          <img
            src={resolveUrl(current.image_url)}
            alt=""
            className="max-w-[90vw] max-h-[70vh] object-contain rounded-lg"
            draggable={false}
          />

          {/* Text + attribution */}
          <div className="mt-6 text-center max-w-sm px-4">
            {current.note && (
              <p className="text-white/80 text-sm italic mb-2">
                "{current.note}"
              </p>
            )}
            {current.guest_name && (
              <p className="text-white/40 text-xs">
                &mdash; {current.guest_name}
              </p>
            )}
          </div>
        </div>
      )}
    </>
  );
}
